/* FIDNO shop — men/women tag filter.
 *
 * Filtering is a tag match against pre-rendered product cards (no refetch):
 * each grid item carries its product's tags in data-fidno-shop-tags, each
 * tab carries the tag it filters to in data-fidno-shop-filter ("all" shows
 * everything). On change, only items actually leaving the visible set fade
 * out, then only items actually entering fade back in as a staggered
 * cascade — cards that stay visible across the change (e.g. Men -> All)
 * are left untouched, never re-animated. No GSAP / reduced motion: the
 * hidden attribute just flips instantly.
 */

(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const motionActive = () =>
    document.documentElement.classList.contains('fidno-motion') && !reducedMotion.matches && window.gsap;

  const tokens = () =>
    (window.FIDNO && window.FIDNO.motion && window.FIDNO.motion.tokens) || {
      fast: 0.2,
      base: 0.35,
      easeOut: 'expo.out',
    };

  function applyFilter(section, filter) {
    const items = Array.from(section.querySelectorAll('[data-fidno-shop-tags]'));
    const matches = items.filter(
      (item) => filter === 'all' || item.dataset.fidnoShopTags.split(',').includes(filter)
    );
    const empty = section.querySelector('.fidno-shop__empty');

    if (!motionActive()) {
      items.forEach((item) => {
        item.hidden = !matches.includes(item);
      });
      if (empty) empty.hidden = matches.length > 0;
      return;
    }

    const gsap = window.gsap;
    const t = tokens();
    const visibleNow = items.filter((item) => !item.hidden);
    /* Only cards actually changing visibility animate; cards staying visible
       across the change (e.g. Men -> All) are left untouched. */
    const leaving = visibleNow.filter((item) => !matches.includes(item));
    const entering = matches.filter((item) => item.hidden);
    gsap.killTweensOf(items);

    const bringIn = () => {
      entering.forEach((item) => {
        item.hidden = false;
      });
      if (empty) empty.hidden = matches.length > 0;
      if (entering.length) {
        gsap.fromTo(
          entering,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: t.base, ease: t.easeOut, stagger: 0.04, clearProps: 'transform,opacity' }
        );
      }
    };

    if (leaving.length) {
      gsap.to(leaving, {
        opacity: 0,
        y: 12,
        duration: t.fast,
        ease: 'power1.in',
        stagger: 0.02,
        onComplete: () => {
          leaving.forEach((item) => {
            item.hidden = true;
          });
          gsap.set(leaving, { clearProps: 'transform,opacity' });
          bringIn();
        },
      });
    } else {
      bringIn();
    }
  }

  function initShop(section) {
    if (section.dataset.fidnoShopInit) return;
    section.dataset.fidnoShopInit = '1';

    const tabs = Array.from(section.querySelectorAll('.fidno-shop__tab'));
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.getAttribute('aria-pressed') === 'true') return;
        tabs.forEach((t) => t.setAttribute('aria-pressed', t === tab ? 'true' : 'false'));
        applyFilter(section, tab.dataset.fidnoShopFilter);
      });
    });
  }

  const initAll = (scope) => (scope || document).querySelectorAll('[data-fidno-shop]').forEach(initShop);

  initAll(document);
  /* Theme editor: sections re-render in place. */
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
